import styled, { css } from 'styled-components';

/**
 * Schedule view, ported from the V4 Program Setup Console prototype (.schedbar / .guide / .gblock / ...).
 * Relies on the `.ps-redesign` design tokens from the Program Setup PsGlobalStyle.
 */

export const HOUR_W = 128;
export const DAY_H = 62;
export const RULER_H = 34;
export const LABEL_W = 112;

/* ---------- Table | Schedule toggle ---------- */
export const ViewTabsWrap = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
`;

export const ViewTab = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 0;
  background: ${({ $on }) => ($on ? 'var(--accent)' : 'none')};
  color: ${({ $on }) => ($on ? '#fff' : 'var(--ink-3)')};
  padding: 7px 15px;
  border-radius: 7px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: ${({ $on }) => ($on ? '#fff' : 'var(--ink)')};
  }

  svg {
    width: 15px;
    height: 15px;
    margin-right: 6px;
  }
`;

export const ChanWarn = styled.button`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: none;
  padding: 0;
  border: 0;
  background: none;
  color: var(--req);
  cursor: pointer;
  align-self: flex-end;
  margin-bottom: 6px;

  svg {
    width: 21px;
    height: 21px;
  }

  &:hover {
    color: #d93a3a;
  }
`;

/* ---------- jump bar + conflict chip ---------- */
export const SchedBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

export const Spacer = styled.span`
  margin-left: auto;
`;

export const JumpGroup = styled.div`
  display: inline-flex;
  gap: 4px;
  flex-wrap: wrap;
`;

export const JumpButton = styled.button<{ $now?: boolean }>`
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: 7px;
  padding: 6px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--accent-ink);
  }

  ${({ $now }) =>
    $now &&
    css`
      &,
      &:hover {
        background: #272b48;
        border-color: #272b48;
        color: #fff;
      }
    `}
`;

export const ConflictChip = styled.button<{ $clean?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  background: ${({ $clean }) => ($clean ? 'var(--ok-soft)' : 'var(--req-soft)')};
  color: ${({ $clean }) => ($clean ? 'var(--ok-ink)' : 'var(--req)')};
  border: 1px solid ${({ $clean }) => ($clean ? 'var(--ok)' : 'var(--req)')};
  cursor: ${({ $clean }) => ($clean ? 'default' : 'pointer')};

  svg {
    width: 13px;
    height: 13px;
  }
`;

/* ---------- guide ---------- */
export const Guide = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  overflow: hidden;
  position: relative;
`;

export const GuideBody = styled.div`
  display: flex;
  align-items: stretch;
`;

export const Labels = styled.div`
  flex: none;
  width: ${LABEL_W}px;
  background: var(--surface);
  border-right: 1px solid var(--line);
  z-index: 2;
`;

export const LabelsHead = styled.div`
  height: ${RULER_H}px;
  border-bottom: 1px solid var(--line);
  background: var(--raise);
`;

export const DayLabel = styled.div<{ $alt?: boolean }>`
  display: flex;
  align-items: center;
  height: ${DAY_H}px;
  padding-left: 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink-2);
  border-bottom: 1px solid var(--line-2);
  background: ${({ $alt }) => ($alt ? 'var(--raise)' : 'transparent')};

  &:last-child {
    border-bottom: 0;
  }

  small {
    display: block;
    font-weight: 500;
    color: var(--ink-4);
    font-size: 10.5px;
  }
`;

export const Scroll = styled.div<{ $panning?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  cursor: ${({ $panning }) => ($panning ? 'grabbing' : 'grab')};
  user-select: ${({ $panning }) => ($panning ? 'none' : 'auto')};
`;

export const Inner = styled.div`
  position: relative;
  width: ${HOUR_W * 24}px;
`;

export const Ruler = styled.div`
  display: flex;
  height: ${RULER_H}px;
  border-bottom: 1px solid var(--line);
  background: var(--raise);
  position: sticky;
  top: 0;
  z-index: 3;
`;

export const Hour = styled.div`
  flex: none;
  width: ${HOUR_W}px;
  border-right: 1px solid var(--line-2);
  font-size: 10.5px;
  font-weight: 700;
  color: var(--ink-4);
  letter-spacing: 0.06em;
  padding: 9px 0 0 8px;
`;

export const DayRow = styled.div<{ $alt?: boolean; $drop?: boolean }>`
  position: relative;
  height: ${DAY_H}px;
  border-bottom: 1px solid var(--line-2);
  background: ${({ $alt, $drop }) => ($drop ? 'var(--accent-soft)' : $alt ? 'var(--raise)' : 'transparent')};

  &:last-child {
    border-bottom: 0;
  }
`;

export const HourLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--line-2);
  pointer-events: none;
`;

export const Block = styled.div<{ $variant: 'ok' | 'conflict' | 'sim'; $dragging?: boolean }>`
  position: absolute;
  top: 7px;
  bottom: 7px;
  border-radius: 7px;
  padding: 6px 9px;
  overflow: hidden;
  background: var(--accent-soft);
  border: 1px solid #bfd0fb;
  color: var(--accent-ink);
  cursor: grab;
  user-select: none;
  min-width: 14px;
  transition: box-shadow 0.14s ease, filter 0.14s ease, border-color 0.14s ease;

  ${({ $variant }) =>
    $variant === 'conflict' &&
    css`
      background: var(--req-soft);
      border-color: var(--req);
      color: var(--req);
    `}
  ${({ $variant }) =>
    $variant === 'sim' &&
    css`
      background: var(--ic-ppl-bg);
      border-color: #c4bffb;
      color: var(--ic-ppl-fg);
    `}

  &:hover {
    filter: brightness(1.02);
    border-color: ${({ $variant }) =>
      $variant === 'conflict' ? 'var(--req)' : $variant === 'sim' ? 'var(--ic-ppl-fg)' : 'var(--accent)'};
    box-shadow: 0 4px 14px -4px rgba(20, 26, 60, 0.35);
  }

  ${({ $dragging }) =>
    $dragging &&
    css`
      cursor: grabbing;
      opacity: 0.85;
      z-index: 9;
      box-shadow: var(--shadow-pop);
    `}

  .bt {
    font-size: 11.5px;
    font-weight: 700;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bs {
    font-size: 10.5px;
    opacity: 0.8;
    white-space: nowrap;
  }

  .cont {
    position: absolute;
    right: 4px;
    top: 4px;
    font-size: 9px;
    opacity: 0.7;
  }

  .hdl {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 7px;
    cursor: ew-resize;
  }

  .hdl.l {
    left: 0;
  }

  .hdl.r {
    right: 0;
  }

  &:hover .hdl {
    background: rgba(0, 0, 0, 0.08);
  }
`;

export const NowLine = styled.div`
  position: absolute;
  top: ${RULER_H}px;
  bottom: 0;
  width: 2px;
  background: var(--req);
  z-index: 5;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--req);
  }
`;

export const GuideFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 11px 16px;
  border-top: 1px solid var(--line);
  font-size: 12px;
  color: var(--ink-3);
`;

export const Legend = styled.span<{ $variant: 'ok' | 'conflict' | 'sim' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  i {
    width: 11px;
    height: 11px;
    border-radius: 3px;
    display: inline-block;
    background: ${({ $variant }) =>
      $variant === 'conflict' ? 'var(--req-soft)' : $variant === 'sim' ? 'var(--ic-ppl-bg)' : 'var(--accent-soft)'};
    border: 1px solid
      ${({ $variant }) => ($variant === 'conflict' ? 'var(--req)' : $variant === 'sim' ? '#c4bffb' : '#bfd0fb')};
  }
`;

export const Muted = styled.span`
  color: var(--ink-4);
`;

export const Empty = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: var(--ink-3);
  font-size: 13.5px;
`;

export const Loading = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 10;
`;

/* ---------- tooltip ---------- */
export const Tip = styled.div`
  position: fixed;
  z-index: 260;
  max-width: 270px;
  background: var(--ink);
  color: #eef1fa;
  border-radius: 9px;
  padding: 10px 12px;
  box-shadow: var(--shadow-pop);
  pointer-events: none;
  font-size: 12px;
  line-height: 1.5;

  .n {
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 5px;
    color: #fff;
  }

  .r {
    display: flex;
    gap: 8px;
  }

  .r span:first-child {
    color: #99a3c8;
    min-width: 58px;
    flex: none;
  }
`;

/* ---------- conflict list ---------- */
export const ConflictListWrap = styled.div`
  margin-top: 16px;
`;

export const ConflictRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 11px 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
  margin-bottom: 8px;

  .d {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--ink-4);
    width: 42px;
  }

  .t {
    font-size: 13px;
    color: var(--ink-2);
  }

  .t b {
    color: var(--ink);
  }

  .t + button {
    margin-left: auto;
  }
`;

export const MiniButton = styled.button`
  border-radius: 7px;
  padding: 6px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: var(--surface);
  border: 1px solid var(--line);
  color: var(--ink-2);
  white-space: nowrap;

  &:hover {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--accent-ink);
  }
`;

export const Hint = styled.div`
  font-size: 11.5px;
  color: var(--ink-4);
  margin-top: 6px;
`;

/* ---------- change confirmation modal ---------- */
export const ModalWrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(12, 16, 38, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const ModalCard = styled.div`
  width: 100%;
  max-width: 470px;
  background: var(--surface);
  border-radius: 14px;
  box-shadow: var(--shadow-pop);
  padding: 24px;
  text-align: center;
`;

export const ModalIc = styled.div`
  width: 46px;
  height: 46px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent-ink);

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const ModalTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const ModalBody = styled.div`
  font-size: 13.5px;
  color: var(--ink-3);
  margin-top: 6px;
  line-height: 1.5;
`;

export const ModalFoot = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;

  button {
    flex: 1;
  }
`;

export const ConfirmNote = styled.div`
  text-align: left;
  background: var(--raise);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 14px;
`;

export const ConfirmLine = styled.div`
  display: flex;
  gap: 10px;
  font-size: 13px;
  margin-top: 6px;

  &:first-child {
    margin-top: 0;
  }

  .k {
    color: var(--ink-4);
    width: 64px;
    flex: none;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    padding-top: 2px;
  }

  .v {
    font-weight: 600;
    color: var(--ink);
  }

  .was {
    color: var(--ink-4);
    text-decoration: line-through;
    font-weight: 500;
  }
`;

export const ScopeBox = styled.div`
  text-align: left;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 12px;

  .scopek {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--ink-4);
    margin-bottom: 8px;
  }
`;

export const ScopeRow = styled.label<{ $off?: boolean }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 7px 0;
  cursor: ${({ $off }) => ($off ? 'not-allowed' : 'pointer')};
  opacity: ${({ $off }) => ($off ? 0.5 : 1)};

  input {
    margin: 3px 0 0;
    width: 15px;
    height: 15px;
    accent-color: var(--accent);
    flex: none;
    cursor: inherit;
  }

  b {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
  }

  small {
    display: block;
    font-size: 11.5px;
    color: var(--ink-4);
    margin-top: 2px;
    line-height: 1.4;
  }
`;

export const SchWarn = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  text-align: left;
  margin-top: 12px;
  padding: 11px 13px;
  border-radius: 10px;
  background: var(--warn-soft);
  color: var(--warn);
  font-size: 12.5px;
  line-height: 1.45;

  svg {
    width: 16px;
    height: 16px;
    flex: none;
    margin-top: 1px;
  }
`;
